import { useEffect, useState } from "react";

// Types
import { TCreateInvitation, TCreateInvitationPayload, TPendingInvitation } from "./InviteUsersModal.types";

// Services
import { setToastConfig } from "store/slice/UI";

// Hooks
import { useAppDispatch } from "app/hooks";

// Utils
import { generateID } from "utils";
import { sendNewInvitation } from "services";

const invInitialState = {
    id: 0,
    name: "",
    email: "",
    org_id: 0,
    message: null,
};

const useInviteUsersModal = (
    pendingInvitations: TPendingInvitation[],
    org_id: number | undefined,
    selectedInvitations: number[],
    setSelectedInvitations: React.Dispatch<React.SetStateAction<number[]>>
): {
    openMessages: Record<number, boolean>;
    anchorEl: { element: HTMLElement | null; id: number | null };
    openSubmenu: boolean;
    invitationsData: TCreateInvitation[];
    filteredInv: TPendingInvitation[];
    areFieldsValid: boolean;
    setAnchorEl: React.Dispatch<React.SetStateAction<{ element: HTMLElement | null; id: number | null }>>;
    setOpenMessages: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
    handleOpenMessage: (index: number) => void;
    handleOnChange: (index: number, field: keyof TCreateInvitation, value: string) => void;
    handleAddInvitations: () => void;
    removeInvitation: (indexToRemove: number) => void;
    handleSend: () => void;
    handleFilterChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleCheckboxChange: (id: number, isChecked: boolean) => void;
    handleSendReminder: () => void;
} => {
    const dispatch = useAppDispatch();

    const [anchorEl, setAnchorEl] = useState<{ element: HTMLElement | null; id: number | null }>({
        element: null,
        id: null,
    });
    const openSubmenu = Boolean(anchorEl.element);

    const [invitationsData, setInvitationsData] = useState<TCreateInvitation[]>([invInitialState]);
    const [filteredInv, setFilteredInv] = useState<TPendingInvitation[]>([]);

    const [openMessages, setOpenMessages] = useState<Record<number, boolean>>({});
    const areFieldsValid = invitationsData.every(
        (invitation) => invitation.name.trim() !== "" && invitation.email.trim() !== ""
    );

    useEffect(() => {
        setFilteredInv(pendingInvitations);
    }, [pendingInvitations]);

    const handleOpenMessage = (id: number) => {
        setOpenMessages((prevOpenMessages) => ({
            ...prevOpenMessages,
            [id]: !prevOpenMessages[id],
        }));
    };

    const handleOnChange = (id: number, field: keyof TCreateInvitation, value: string) => {
        setInvitationsData((prevInv) =>
            prevInv.map((invitation) => (invitation.id === id ? { ...invitation, [field]: value } : invitation))
        );
    };

    const handleAddInvitations = () => {
        const newId = generateID();
        setInvitationsData((prevInv) => {
            const newInvitation = { ...invInitialState, id: newId };

            return [...prevInv, newInvitation];
        });

        setOpenMessages((prevOpenMessages) => ({
            ...prevOpenMessages,
            [newId]: false,
        }));
    };

    const removeInvitation = (idToRemove: number) => {
        setInvitationsData((prevInvitations) => prevInvitations.filter((invitation) => invitation.id !== idToRemove));

        setOpenMessages((prevOpenMessages) => {
            const updatedMessages = { ...prevOpenMessages };
            delete updatedMessages[idToRemove];
            return updatedMessages;
        });
    };

    const handleCheckboxChange = (id: number, isChecked: boolean) => {
        setSelectedInvitations((prev) => (isChecked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id)));
    };

    const handleSend = async () => {
        const invites = invitationsData.map((invitation) => ({
            name: invitation.name,
            email: invitation.email,
            org_id,
            message: invitation.message || null,
        }));
        try {
            await sendNewInvitation({ invites });
            dispatch(
                setToastConfig({
                    title: "User invite sent",
                    content: "To manage pending invitations, check the link above.",
                })
            );
            setInvitationsData([invInitialState]);

            const newPendingInvitations: TPendingInvitation[] = invites.map((invite) => ({
                id: generateID(),
                sent_utc: new Date().toISOString(),
                recipient_email: invite.email,
                recipient_name: invite.name,
            }));

            setFilteredInv((prevFilteredInv) => [...newPendingInvitations, ...prevFilteredInv]);

            if (typeof pendingInvitations !== "undefined") {
                pendingInvitations.unshift(...newPendingInvitations);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleSendReminder = async () => {
        const invitesToResend: TCreateInvitationPayload[] = filteredInv
            .filter((inv) => selectedInvitations.includes(inv.id))
            .map((inv) => ({
                name: inv.recipient_name,
                email: inv.recipient_email,
                org_id,
                message: null,
            }));

        if (invitesToResend.length === 0) return;

        try {
            await sendNewInvitation({ invites: invitesToResend });

            setSelectedInvitations([]);
        } catch (error) {
            console.log(error);
        }
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value.toLowerCase();
        setFilteredInv(
            pendingInvitations.filter(
                (user) =>
                    user.recipient_name.toLowerCase().includes(text) ||
                    user.recipient_email.toLowerCase().includes(text)
            )
        );
    };

    return {
        openMessages,
        anchorEl,
        openSubmenu,
        invitationsData,
        filteredInv,
        areFieldsValid,
        setAnchorEl,
        setOpenMessages,
        handleOpenMessage,
        handleOnChange,
        handleAddInvitations,
        removeInvitation,
        handleSend,
        handleFilterChange,
        handleCheckboxChange,
        handleSendReminder,
    };
};

export default useInviteUsersModal;
